import { useFetcher, useOutletContext } from "@remix-run/react";
import { ShirtspaceResponseMessage } from "@services/shirtspace.types";
import get from "lodash/get";
import { useEffect, useState } from "react";
import FlashMessage from "~/components/FlashMessage";
import { Close } from "~/components/Modal";
import { AccountSurveyActionResponse } from "~/entities/actions/types";
import { StoreLoaderContext } from "~/entities/global/types";
import { MEMBER_QUESTION_ID } from "~/entities/survey/constants";
import RadioField from "./RadioField";
import SurveyQuestion from "./SurveyQuestion";

const ErrorMessage: React.FC<{
  error?: ShirtspaceResponseMessage;
}> = ({ error }) => {
  if (!error) return null;
  return (
    <div className="mt-[-1rem]">
      <FlashMessage
        kind="failure"
        message={error.text}
      />
    </div>
  );
};

type Props = {
  onSuccess: () => void;
  isActive: boolean;
};

const MemberSurvey: React.FC<Props> = ({ onSuccess, isActive }) => {
  const [errors, setErrors] = useState<ShirtspaceResponseMessage[]>([]);

  const context = useOutletContext<StoreLoaderContext>();
  const fetcher = useFetcher<AccountSurveyActionResponse>();

  useEffect(() => {
    if (get(fetcher, "data.ok") === true) {
      setErrors([]);
      onSuccess();
    } else {
      setErrors(get(fetcher, "data.messages", []));
    }
  }, [fetcher.data]);

  if (!isActive) return null;

  return (
    <>
      <Close onClick={() => context.setCreateAccountWizardOpen(false)} />
      <fetcher.Form
        method="post"
        action="/resources/create-account-wizard"
        className="flex flex-col gap-7 xsm:gap-4 md:gap-3.5 px-5"
        data-testid="member-survey-form"
      >
        <input
          type="hidden"
          name="intent"
          value="member_survey"
        />
        <h2 className="text-[20px] text-blue900 font-sans mt-0 uppercase font-bold">
          Customer survey
        </h2>

        <ErrorMessage error={errors.find((error) => error.type === "error")} />

        <SurveyQuestion
          ordinal={1}
          question="Where did you hear about ShirtSpace.com?"
        >
          <RadioField
            id="search_engine"
            name={MEMBER_QUESTION_ID}
            value="479"
          >
            Search engine
          </RadioField>
          <RadioField
            id="social_media"
            name={MEMBER_QUESTION_ID}
            value="480"
          >
            Social media
          </RadioField>
          <RadioField
            id="word_of_mouth"
            name={MEMBER_QUESTION_ID}
            value="481"
          >
            Word of mouth
          </RadioField>
          <RadioField
            id="other"
            name={MEMBER_QUESTION_ID}
            value="482"
          >
            Other
          </RadioField>
        </SurveyQuestion>
        <button
          type="submit"
          className="h-8 w-auto self-start rounded-md bg-blue hover:bg-blue200 disabled:bg-blue500 px-4 text-lg uppercase text-white mb-[-10px]"
          disabled={fetcher.state === "submitting"}
        >
          {fetcher.state === "submitting" ? "Submitting..." : "Almost done"}
        </button>
      </fetcher.Form>
    </>
  );
};

export default MemberSurvey;
